import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import {
  createMessage,
  createThread,
  filterThreads,
  initServerChatHistorySync,
  loadChatHistory,
  renameThread as renameThreadInList,
  saveChatHistory,
  syncThreadToServer,
  truncateThreadFrom,
  upsertThreadMessage,
  type ChatMessage,
  type ChatThread,
} from '../lib/chat-history'
import type { RagAskResponse } from '../types/api'

function findThread(threads: ChatThread[], threadId: string): ChatThread | undefined {
  return threads.find((thread) => thread.id === threadId)
}

export function useChatHistory() {
  const { me } = useAuth()
  const userId = me?.user.user_id ?? null
  const [threads, setThreads] = useState<ChatThread[]>([])
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (!userId) {
      setThreads([])
      setActiveThreadId(null)
      setReady(false)
      return
    }

    let cancelled = false
    const snapshot = loadChatHistory(userId)
    setThreads(snapshot.threads)
    setActiveThreadId(snapshot.activeThreadId)
    setReady(true)

    // CX7: ローカルを即時表示したうえで、移行 + サーバー優先スナップショットへ非同期同期
    void initServerChatHistorySync(userId)
      .then((serverSnapshot) => {
        if (cancelled) {
          return
        }
        setThreads(serverSnapshot.threads)
        setActiveThreadId(serverSnapshot.activeThreadId)
      })
      .catch((err) => {
        console.warn('[useChatHistory] initServerChatHistorySync failed; keeping local history', err)
      })

    return () => {
      cancelled = true
    }
  }, [userId])

  useEffect(() => {
    if (!userId || !ready) {
      return
    }

    saveChatHistory(userId, { version: 1, threads, activeThreadId })
  }, [userId, ready, threads, activeThreadId])

  const activeThread = useMemo(
    () => threads.find((thread) => thread.id === activeThreadId) ?? null,
    [threads, activeThreadId],
  )

  const filteredThreads = useMemo(
    () => filterThreads(threads, searchQuery),
    [threads, searchQuery],
  )

  const messages = activeThread?.messages ?? []

  const startNewThread = useCallback(() => {
    const thread = createThread()
    setThreads((current) => [thread, ...current])
    setActiveThreadId(thread.id)
    return thread.id
  }, [])

  const selectThread = useCallback((threadId: string) => {
    setActiveThreadId(threadId)
  }, [])

  const deleteThread = useCallback((threadId: string) => {
    setThreads((current) => {
      const next = current.filter((thread) => thread.id !== threadId)
      setActiveThreadId((activeId) => {
        if (activeId !== threadId) {
          return activeId
        }
        return next[0]?.id ?? null
      })
      return next
    })
  }, [])

  const renameThread = useCallback((threadId: string, rawTitle: string) => {
    setThreads((current) => renameThreadInList(current, threadId, rawTitle))
  }, [])

  const ensureActiveThread = useCallback(() => {
    if (activeThreadId && threads.some((thread) => thread.id === activeThreadId)) {
      return activeThreadId
    }

    const thread = createThread()
    setThreads((current) => [thread, ...current])
    setActiveThreadId(thread.id)
    return thread.id
  }, [activeThreadId, threads])

  const appendUserMessage = useCallback(
    (content: string) => {
      const threadId = ensureActiveThread()
      const message = createMessage('user', content)
      setThreads((current) => {
        const next = upsertThreadMessage(current, threadId, message, content)
        if (userId) {
          const thread = findThread(next, threadId)
          if (thread) {
            void syncThreadToServer(userId, thread, message)
          }
        }
        return next
      })
      return threadId
    },
    [ensureActiveThread, userId],
  )

  const appendAssistantMessage = useCallback(
    (threadId: string, content: string, response: RagAskResponse) => {
      const message = createMessage('assistant', content, response)
      setThreads((current) => {
        const next = upsertThreadMessage(current, threadId, message)
        if (userId) {
          const thread = findThread(next, threadId)
          if (thread) {
            void syncThreadToServer(userId, thread, message)
          }
        }
        return next
      })
      return message
    },
    [userId],
  )

  const editUserMessage = useCallback(
    (threadId: string, messageId: string) => {
      const thread = threads.find((t) => t.id === threadId)
      const message = thread?.messages.find((m) => m.id === messageId)
      if (!message) {
        return null
      }
      setThreads((current) => truncateThreadFrom(current, threadId, messageId))
      return message.content
    },
    [threads],
  )

  return {
    ready,
    threads,
    activeThreadId,
    messages,
    searchQuery,
    setSearchQuery,
    filteredThreads,
    startNewThread,
    selectThread,
    deleteThread,
    renameThread,
    appendUserMessage,
    appendAssistantMessage,
    editUserMessage,
  }
}

export type { ChatMessage, ChatThread }
